import { useEffect, useRef } from 'preact/hooks';

export const useLatestValueRef = <T>(val: T) => {
  const ref = useRef(val);

  useEffect(() => {
    ref.current = val;
  }, [val]);

  return ref as { readonly current: T };
};

/**
 * Like {@link useLatestValueRef}, but writes the ref during render instead of in
 * an effect, so `current` is always the latest value — including during the same
 * render and in layout effects — with no one-commit lag and no extra effect or
 * dependency compare.
 *
 * "Dangerous" because writing to a ref during render is an impure render side
 * effect. It is safe in Preact (synchronous rendering, no concurrent mode and no
 * discarded/retried renders) but would be unsafe under React concurrent
 * rendering. Only use it for a latest-value ref that is read in event handlers
 * or effects — never to derive rendered output.
 */
export const useDangerousLatestValueRef = <T>(val: T) => {
  const ref = useRef(val);
  ref.current = val;
  return ref as { readonly current: T };
};
